import first from "lodash/first";
import upperFirst from "lodash/upperFirst";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { article as articleData, config, section as sectionData } from "~/data";
import type { articleSeoArticle$data } from "~/queries/__generated__/articleSeoArticle.graphql";
import type { hooksArticleSeoArticle$key } from "~/queries/__generated__/hooksArticleSeoArticle.graphql";

import { ExcludedArticleIds, ValidArticleFlag } from "./consts";

export const useArticleSeoHelper = (article_: articleSeoArticle$data) => {
  const article = useFragment<hooksArticleSeoArticle$key>(
    graphql`
      fragment hooksArticleSeoArticle on Article {
        entityId
        flag
        knowledgeQuestion
        headline
        socialHeadline
        summary {
          text
        }
        types {
          value {
            entityId
          }
        }
        topics {
          sponsor {
            entityId
          }
        }
        sections {
          value {
            entityId
          }
        }
        images {
          generic_og: style(filter: { style: "og_image_scmp_generic" }) {
            url
          }
          styles(
            filter: {
              styles: [
                "og_image_scmp_analysis"
                "og_image_scmp_explainer"
                "og_image_scmp_fact_check"
                "og_image_scmp_debate"
                "og_image_scmp_live"
                "og_image_scmp_obituary"
                "og_image_scmp_opinion"
                "og_image_scmp_review"
                "og_image_scmp_series"
                "og_image_scmp_generic"
              ]
            }
          ) {
            url
            style
          }
          type
        }
        disableOffPlatformContent
        published
        sponsorType
        knowledgeQuestion
        urlAlias
      }
    `,
    article_,
  );

  const computedMetaHeadline = useMemo(() => {
    const firstArticleTypeEntityID =
      first(article.types?.flatMap(type => type?.value))?.entityId ?? "";

    const sanitizedHeadline = article.headline ?? "";

    if (
      firstArticleTypeEntityID === articleData.types.column.entityId ||
      firstArticleTypeEntityID === articleData.types.opinion.entityId
    ) {
      return `Opinion | ${sanitizedHeadline}`;
    } else if (
      article.knowledgeQuestion &&
      firstArticleTypeEntityID !== articleData.types.series.entityId
    ) {
      return article.knowledgeQuestion;
    } else if (article.flag && ValidArticleFlag.includes(article.flag)) {
      const sanitizedFlag = upperFirst(article.flag.trim().toLowerCase());
      return `${sanitizedFlag}: ${sanitizedHeadline}`;
    }

    return `${sanitizedHeadline}`;
  }, [article.flag, article.headline, article.knowledgeQuestion, article.types]);

  const isAmpMetaTagRequired = useMemo(() => {
    const baseArticleTypeData = article.types?.flatMap(type => type?.value) ?? [];

    const baseArticleSectionData = article.sections?.flatMap(section => section?.value) ?? [];

    const isNotASponsoredTopic = !article.topics?.some(topic => topic?.sponsor?.entityId);

    const isNotSponsorType = !article.sponsorType;

    const isPublished = article.published;

    const isNotInfographicsArticle = !(
      baseArticleSectionData.some(
        section => section?.entityId === sectionData.infographics.entityId,
      ) ||
      baseArticleTypeData.some(type => type?.entityId === articleData.types.infographic.entityId)
    );

    const isNotSeriesArticle =
      first(baseArticleTypeData)?.entityId !== articleData.types.series.entityId;

    const isNotNewsletterTypeArticle =
      first(baseArticleTypeData)?.entityId !== articleData.types.newsletter.entityId;

    const isNotExcludedArticle = !ExcludedArticleIds.includes(article.entityId);

    const isNotAnnouncementArticle = article.entityId !== "3119450";

    const isNotDisableAmp = !(article.disableOffPlatformContent ?? []).includes("amp");

    const isNotArticleLiveType =
      first(baseArticleTypeData)?.entityId !== articleData.types.live.entityId;

    return (
      isNotASponsoredTopic &&
      isNotSponsorType &&
      isPublished &&
      isNotInfographicsArticle &&
      isNotSeriesArticle &&
      isNotNewsletterTypeArticle &&
      isNotExcludedArticle &&
      isNotAnnouncementArticle &&
      isNotDisableAmp &&
      isNotArticleLiveType
    );
  }, [
    article.disableOffPlatformContent,
    article.entityId,
    article.published,
    article.sections,
    article.sponsorType,
    article.topics,
    article.types,
  ]);

  const ogMetaDataImage = useMemo(() => {
    const articleTypeId = first(article.types?.flatMap(type => type?.value))?.entityId ?? "";

    const newMap = {
      [articleData.types.analysis.entityId]: "_analysis",
      [articleData.types.explainer.entityId]: "_explainer",
      [articleData.types.factCheck.entityId]: "_face_check",
      [articleData.types.debate.entityId]: "_debate",
      [articleData.types.live.entityId]: "_live",
      [articleData.types.obituary.entityId]: "_obituary",
      [articleData.types.column.entityId]: "_opinion",
      [articleData.types.opinion.entityId]: "_opinion",
      [articleData.types.review.entityId]: "_review",
      [articleData.types.series.entityId]: "_series",
    } as Record<string, string>;

    const validImageSet =
      article.images?.find(imageSet => imageSet?.type === "default") ?? article.images?.[0];

    const metaImage = validImageSet?.styles?.find(set => {
      if (set?.style?.includes(newMap[articleTypeId]) && set?.style?.includes("og_image_"))
        return set;
    });

    return metaImage?.url ?? validImageSet?.generic_og?.url ?? "";
  }, [article.images, article.types]);

  const openGraphMetaData = useMemo(
    () => ({
      description: article.summary?.text ?? article.headline ?? "",
      images: [{ url: ogMetaDataImage }],
      title: article.knowledgeQuestion ?? article.socialHeadline ?? article.headline ?? "",
      type: "article",
      url: `${config.general.siteUrl}${article.urlAlias}`,
    }),
    [
      article.headline,
      article.knowledgeQuestion,
      article.socialHeadline,
      article.summary?.text,
      article.urlAlias,
      ogMetaDataImage,
    ],
  );

  return {
    computedMetaHeadline,
    isAmpMetaTagRequired,
    openGraphMetaData,
  };
};
